import { useAppSettingHelper } from '@affine/core/components/hooks/affine/use-app-setting-helper';
import { RootAppSidebar } from '@affine/core/components/root-app-sidebar';
import { AppSidebarService } from '@affine/core/modules/app-sidebar';
import {
  AppSidebarFallback,
  // OpenInAppCard,
  SidebarSwitch,
} from '@affine/core/modules/app-sidebar/views';
import { AppTabsHeader } from '@affine/core/modules/app-tabs-header';
import { NavigationButtons } from '@affine/core/modules/navigation';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import clsx from 'clsx';
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
} from 'react';

import * as styles from './styles.css';
import { useTheme } from '@affine/core/modules/theme/use-theme';
import { useI18n } from '@affine/i18n';
import { useMyMessage } from '@ys/repo-ui/base';
import {
  getCrossTabCommunication,
  CrossTabMessageTypes,
} from '@affine/core/utils/cross-tab-communication';
import { IndexedDBError } from '@affine/nbstore';
import { notify } from '@affine/component';
import { subscriptionSuccessTrack } from '@affine/core/utils/ga-tracking';
import { aliTrack } from '@affine/track';
import { usePreloadedBlackFriday } from '@affine/core/components/hooks/use-preloaded-black-friday';
import { toast } from 'sonner';

export const AppContainer = ({
  children,
  className,
  fallback = false,
  beforeCloseCurrentTab,
  ...rest
}: PropsWithChildren<{
  className?: string;
  fallback?: boolean;
  beforeCloseCurrentTab?: () => Promise<boolean>;
}>) => {
  const { appSettings } = useAppSettingHelper();
  const { currentTheme } = useTheme();

  const noisyBackground =
    BUILD_CONFIG.isElectron && appSettings.enableNoisyBackground;
  const blurBackground =
    BUILD_CONFIG.isElectron &&
    environment.isMacOs &&
    appSettings.enableBlurBackground;
  return (
    <div
      {...rest}
      className={clsx(styles.appStyle, styles.appContainerStyles, className, {
        'noisy-background': noisyBackground,
        'blur-background': blurBackground,
      })}
      data-noise-background={noisyBackground}
      data-translucent={blurBackground}
      style={
        {
          '--theme-background-color': currentTheme.colors.backgroundColor,
        } as React.CSSProperties
      }
    >
      <LayoutComponent
        fallback={fallback}
        beforeCloseCurrentTab={beforeCloseCurrentTab}
      >
        {children}
      </LayoutComponent>
    </div>
  );
};

const DesktopLayout = ({
  children,
  fallback = false,
}: PropsWithChildren<{ fallback?: boolean }>) => {
  const workspaceService = useServiceOptional(WorkspaceService);
  const isInWorkspace = !!workspaceService;
  return (
    <div className={styles.desktopAppViewContainer}>
      <div className={styles.desktopTabsHeader}>
        <AppTabsHeader
          left={
            <>
              {isInWorkspace && <SidebarSwitch show />}
              {isInWorkspace && <NavigationButtons />}
            </>
          }
        />
      </div>
      <div className={styles.desktopAppViewMain}>
        {fallback ? (
          <AppSidebarFallback />
        ) : (
          isInWorkspace && <RootAppSidebar />
        )}
        <MainContainer>{children}</MainContainer>
      </div>
    </div>
  );
};

const BrowserLayout = ({
  children,
  fallback = false,
  beforeCloseCurrentTab,
}: PropsWithChildren<{
  fallback?: boolean;
  beforeCloseCurrentTab?: () => Promise<boolean>;
}>) => {
  const workspaceService = useServiceOptional(WorkspaceService);

  const { info, MessageContextHolder } = useMyMessage();
  // const authService = useService(AuthService);
  const isInWorkspace = !!workspaceService;
  const [indexDBError, setIndexDBError] = useState(false);
  const hasToastRef = useRef(false);
  // const account = useLiveData(authService.session.account$);
  const t = useI18n();
  const handleIndexDBError = (event: ErrorEvent | PromiseRejectionEvent) => {
    console.error('捕获到全局错误:', event);
    event.preventDefault();
    aliTrack('unHandleError', {
      windowError: event,
      windowErrorMessage: `${'error' in event ? event.error.message : event.reason.message}`,
    });
    if (
      ('error' in event &&
        event.error &&
        ((event.error.message &&
          event.error.message.includes(
            'Internal error opening backing store for indexedDB.open.'
          )) ||
          event.error instanceof IndexedDBError)) ||
      ('reason' in event &&
        event.reason &&
        event.reason.message?.includes(
          'Internal error opening backing store for indexedDB.open.'
        ))
    ) {
      console.warn('🚨 检测到IndexedDB错误，触发重载界面');
      setIndexDBError(true);

      // 通知其他Tab发生了IndexedDB错误
      const crossTab = getCrossTabCommunication();
      crossTab.sendMessage(CrossTabMessageTypes.INDEXEDDB_ERROR, {
        error: 'IndexedDB error detected',
        errorType: 'failed to connect or backing store',
        timestamp: Date.now(),
        url: window.location.href,
      });
    }
  };
  useEffect(() => {
    // 初始化跨Tab通信
    const crossTab = getCrossTabCommunication();

    // 监听来自其他Tab的IndexedDB错误通知
    const unsubscribeIndexedDBError = crossTab.onMessage(
      CrossTabMessageTypes.INDEXEDDB_ERROR,
      message => {
        console.warn('📨 收到其他Tab的IndexedDB错误通知:', message.data);
        setIndexDBError(true);
      }
    );

    // 监听关闭请求
    const unsubscribeCloseRequest = crossTab.onMessage(
      CrossTabMessageTypes.CLOSE_REQUEST,
      message => {
        console.log('📨 收到关闭请求:', message.data);

        // 尝试关闭窗口，如果失败则重定向到登录页
        try {
          window.close();
          // 如果 window.close() 没有立即关闭窗口，说明可能是用户直接打开的窗口
          // 延迟检查窗口是否仍然存在
          setTimeout(() => {
            if (!window.closed) {
              console.log('无法关闭窗口，重定向到登录页');
              window.location.href = `${window.location.origin}/sign-in`;
            }
          }, 100);
        } catch (error) {
          console.log('关闭窗口失败，重定向到登录页:', error);
          window.location.href = `${window.location.origin}/sign-in`;
        }
      }
    );
    const channel = new BroadcastChannel('indexeddb-errors');
    channel.onmessage = async event => {
      if (event.data.type === 'INDEXEDDB_ERROR') {
        console.warn('🚨 通过BroadcastChannel检测到IndexedDB错误');
        const result = await beforeCloseCurrentTab?.();
        console.log('aaaaaaaaa，清理缓存', 'result', result);
        window.location.href = `${window.location.origin}/sign-in`;
      }
    };

    // 捕获同步错误
    window.addEventListener('error', handleIndexDBError);
    // 专门用于捕获未处理的 Promise 拒绝
    window.addEventListener('unhandledrejection', handleIndexDBError);
    return () => {
      window.removeEventListener('error', handleIndexDBError);
      window.removeEventListener('unhandledrejection', handleIndexDBError);
      channel.close();
      unsubscribeIndexedDBError();
      unsubscribeCloseRequest();
    };
  }, []);
  usePreloadedBlackFriday();
  useEffect(() => {
    const url = new URL(window.location.href);

    if (url.searchParams.has('subscribSuccess')) {
      const subscribSuccess = url.searchParams.get('subscribSuccess');
      const query = JSON.parse(decodeURIComponent(subscribSuccess || '{}'));
      if (
        query.addonBuySuccess &&
        query.addonBuySuccess === 'true' &&
        !hasToastRef.current
      ) {
        toast.success(`${t['com.muset.payment.add-on.success.toast']()}`);
        hasToastRef.current = true;
      }
      subscriptionSuccessTrack(query).then(() => {
        url.searchParams.delete('subscribSuccess');

        window.history.replaceState(
          {},
          '',
          url.pathname + url.search + url.hash
        );
      });
    }
  }, [window.location.href, subscriptionSuccessTrack]);

  const handleReload = async () => {
    console.log('aaaaaaaaa，清理缓存', 'handleReload', beforeCloseCurrentTab);
    info(t['com.affine.indexDB.error.cleaning']());
    const result = await beforeCloseCurrentTab?.();
    console.log('aaaaaaaaa，清理缓存', 'result', result);
    notify.success({
      message: t['com.affine.indexDB.error.success'](),
    });

    // 通知其他Tab进行关闭
    const crossTab = getCrossTabCommunication();
    crossTab.sendMessage(CrossTabMessageTypes.CLOSE_REQUEST, {
      reason: 'IndexedDB error detected, requesting close',
      timestamp: Date.now(),
      url: window.location.href,
    });

    // 先打开新的 tab
    // window.open(`${window.location.origin}/sign-in`, '_blank');
    window.close();
    // 延迟关闭当前 tab，确保新窗口已经打开
    // setTimeout(() => {
    //   try {
    //     window.close();
    //   } catch (error) {
    //     console.log('关闭窗口失败，重定向到登录页:', error);
    //     window.location.href = `${window.location.origin}/sign-in`;
    //   }
    // }, 100);
  };
  return (
    <>
      {MessageContextHolder}
      {indexDBError || fallback ? (
        <div className={styles.browserAppViewLoading}>
          <img
            className={styles.loadingImage}
            src={
              indexDBError
                ? 'https://wy-static.wenxiaobai.com/wenxiaobai-web/web-image/muset/reloadPageIcon.png'
                : 'https://wy-static.wenxiaobai.com/wenxiaobai-web/web-image/muset/loadingIconNew.webp'
            }
          />
          {indexDBError && (
            <>
              <div className={styles.indexDBErrorText}>
                {t['com.affine.indexDB.error']()}
              </div>
              <div
                className={styles.indexDBErrorReloadButton}
                onClick={handleReload}
              >
                {t['com.affine.indexDB.error.reload']()}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={styles.browserAppViewContainer}>
          {/* <OpenInAppCard /> */}
          {fallback ? (
            <AppSidebarFallback />
          ) : (
            isInWorkspace && <RootAppSidebar />
          )}
          <MainContainer>{children}</MainContainer>
        </div>
      )}
    </>
  );
};

const LayoutComponent = BUILD_CONFIG.isElectron ? DesktopLayout : BrowserLayout;

const MainContainer = forwardRef<
  HTMLDivElement,
  PropsWithChildren<HTMLAttributes<HTMLDivElement>>
>(function MainContainer({ className, children, ...props }, ref): ReactElement {
  const workspaceService = useServiceOptional(WorkspaceService);
  const isInWorkspace = !!workspaceService;
  const { appSettings } = useAppSettingHelper();
  const appSidebarService = useService(AppSidebarService).sidebar;
  const open = useLiveData(appSidebarService.open$);

  return (
    <div
      {...props}
      className={clsx(styles.mainContainerStyle, className)}
      data-is-desktop={BUILD_CONFIG.isElectron}
      data-transparent={false}
      data-client-border={appSettings.clientBorder}
      data-side-bar-open={open && isInWorkspace}
      data-testid="main-container"
      ref={ref}
    >
      {children}
    </div>
  );
});

MainContainer.displayName = 'MainContainer';
